// ============================================
// CONTEXTO DE AUTENTICAÇÃO
// Gerencia login, logout e controle de acesso
// ============================================

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/environment';
import { markFreshLoginForZoomOnboarding } from '../utils/browserZoomOnboarding';
import { clearPlatformContractSnooze } from '../utils/platformContractSnooze';
import { SystemRole, hasUserRoutePermission, getDefaultRoute, getRoleLabel } from '../config/permissions';
import type { UserGender } from '../constants/userAvatars';
import { inboxConversationCache } from '../services/inboxConversationCache';
import { markNativeLogout, takeNativeHandoffToken } from '../utils/nativeApp';

// ============================================
// TIPOS
// ============================================

export interface UnitContextSummary {
  id: string;
  name: string;
  short_name: string;
  is_matrix: boolean;
  can_manage: boolean;
  is_default: boolean;
  /** Inboxes WhatsApp permitidos nesta unidade (null = todos). */
  allowed_whatsapp_inbox_paths?: string[] | null;
}

/** Admin master por trás de uma sessão de "acessar como". */
export interface ImpersonationActor {
  id: string;
  name: string;
  email: string;
}

export interface AuthUser {
  id: string;
  email: string;
  full_name: string;
  role: SystemRole;
  professional_id: string | null;
  avatar_url: string | null;
  gender: UserGender | null;
  phone: string | null;
  is_active: boolean;
  must_change_password: boolean;
  two_factor_enabled?: boolean;
  last_login_at: string | null;
  preferences: Record<string, any>;
  custom_permissions?: Record<string, unknown> | null;
  professional?: {
    id: string;
    full_name: string;
    specialty: string;
    registration_number: string;
    registration_type: string;
    avatar_url: string | null;
    metadata?: Record<string, unknown> | null;
    can_access_dynamic_inbox?: boolean;
    agenda_view_other_professionals_enabled?: boolean;
    agenda_viewable_professional_ids?: string[];
  };
  // Multi-unidade
  current_unit?: UnitContextSummary | null;
  available_units?: UnitContextSummary[];
  // Presente apenas em sessões abertas via "acessar como"
  impersonated_by?: ImpersonationActor | null;
  session_flags?: {
    admin_master_peer_isolation?: boolean;
    whatsapp_inbox_user_access_enabled?: boolean;
  };
  /** Agendas privadas que este login não pode ver (a API já filtra; a grade esconde as colunas). */
  agenda_hidden_professional_ids?: string[];
}

/** Com 2FA ativo, a senha certa devolve um desafio e o login termina em verifyTwoFactor. */
export type LoginResult =
  | { requiresTwoFactor: false }
  | { requiresTwoFactor: true; challengeToken: string };

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  needsSetup: boolean;
  
  // Actions
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyTwoFactor: (challengeToken: string, code: string) => Promise<void>;
  logout: () => void;
  setup: (data: { email: string; password: string; full_name: string; setup_key: string }) => Promise<void>;
  refreshUser: () => Promise<void>;
  switchUnit: (unitId: string) => Promise<void>;
  impersonateUser: (userId: string) => Promise<void>;
  stopImpersonation: () => void;

  // Helpers
  isAdmin: boolean;
  impersonatedBy: ImpersonationActor | null;
  isProfessional: boolean;
  hasPermission: (path: string) => boolean;
  roleLabel: string;
  defaultRoute: string;
  currentUnit: UnitContextSummary | null;
  availableUnits: UnitContextSummary[];
}

// ============================================
// CONTEXTO
// ============================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Sessão do admin guardada durante um "acessar como", para voltar sem novo login.
const IMPERSONATION_ORIGIN_TOKEN = 'impersonation_origin_token';
const IMPERSONATION_ORIGIN_USER = 'impersonation_origin_user';

// ============================================
// PROVIDER
// ============================================

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  // ============================================
  // Inicialização - verificar token salvo
  // ============================================

  useEffect(() => {
    const resolveSession = async (candidate: string): Promise<AuthUser | null> => {
      try {
        const response = await axios.get(`${getApiUrl()}/auth/me`, {
          headers: { Authorization: `Bearer ${candidate}` },
        });
        if (response.data.success && response.data.data) return response.data.data;
        return null;
      } catch {
        return null;
      }
    };

    const initAuth = async () => {
      try {
        // App iOS: o login foi feito na tela nativa e o token veio na URL.
        const handoffToken = takeNativeHandoffToken();
        if (handoffToken) {
          const handoffUser = await resolveSession(handoffToken);
          if (handoffUser) {
            localStorage.removeItem(IMPERSONATION_ORIGIN_TOKEN);
            localStorage.removeItem(IMPERSONATION_ORIGIN_USER);
            await finishLogin({ user: handoffUser, token: handoffToken });
            return;
          }
        }

        const savedToken = localStorage.getItem('auth_token');
        const savedUser = localStorage.getItem('auth_user');

        if (savedToken && savedUser) {
          const resolved = await resolveSession(savedToken);

          if (resolved) {
            setToken(savedToken);
            setUser(resolved);
            setNeedsSetup(false);
          } else {
            // Token de "acessar como" expira em 1h; nesse caso volta para a
            // sessão do admin em vez de derrubar o login inteiro.
            const originToken = localStorage.getItem(IMPERSONATION_ORIGIN_TOKEN);
            const originUser = originToken ? await resolveSession(originToken) : null;

            if (originToken && originUser) {
              localStorage.removeItem(IMPERSONATION_ORIGIN_TOKEN);
              localStorage.removeItem(IMPERSONATION_ORIGIN_USER);
              applySession(originToken, originUser);
              setNeedsSetup(false);
            } else {
              clearAuth();
              await checkSetup();
            }
          }
        } else {
          // Sem token salvo - verificar se precisa de setup
          await checkSetup();
        }
      } catch (error) {
        console.error('Auth init error:', error);
        clearAuth();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // ============================================
  // Verificar se o setup é necessário
  // ============================================

  const checkSetup = async () => {
    try {
      const response = await axios.get(`${getApiUrl()}/auth/setup-status`);
      if (response.data.success) {
        setNeedsSetup(response.data.data.needs_setup);
      }
    } catch (error) {
      console.error('Setup check error:', error);
      // Se não conseguir verificar, assume que não precisa de setup
      setNeedsSetup(false);
    }
  };

  // ============================================
  // Login
  // ============================================

  const finishLogin = useCallback(async (loginData: { user: AuthUser; token: string }) => {
    const { user: userData, token: authToken } = loginData;

    setToken(authToken);
    setUser(userData);
    setNeedsSetup(false);

    // Salvar no localStorage
    localStorage.setItem('auth_token', authToken);
    localStorage.setItem('auth_user', JSON.stringify(userData));

    // Compatibilidade com sistema antigo
    localStorage.setItem('token', authToken);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('user', JSON.stringify({
      id: userData.id,
      name: userData.full_name,
      email: userData.email,
    }));

    // Restaura "última unidade" salva da sessão anterior se diferente da default
    // e o usuário ainda tem acesso. Só atualiza last_unit_id depois de resolver.
    let resolvedUnitId: string | undefined = userData?.current_unit?.id;
    try {
      const lastUnitId = localStorage.getItem('last_unit_id');
      const availableIds: string[] = (userData.available_units || []).map((u: any) => u.id);
      if (
        lastUnitId &&
        availableIds.includes(lastUnitId) &&
        resolvedUnitId !== lastUnitId
      ) {
        const switchResp = await axios.post(
          `${getApiUrl()}/auth/switch-unit`,
          { unit_id: lastUnitId },
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        if (switchResp.data?.success && switchResp.data.data) {
          const { user: u2, token: t2 } = switchResp.data.data;
          setToken(t2);
          setUser(u2);
          localStorage.setItem('auth_token', t2);
          localStorage.setItem('auth_user', JSON.stringify(u2));
          localStorage.setItem('token', t2);
          resolvedUnitId = u2?.current_unit?.id || resolvedUnitId;
        }
      }
    } catch {
      // Falha ao restaurar última unidade não bloqueia o login
    }
    if (resolvedUnitId) {
      localStorage.setItem('last_unit_id', resolvedUnitId);
    }

    markFreshLoginForZoomOnboarding();
    clearPlatformContractSnooze();
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await axios.post(`${getApiUrl()}/auth/login`, {
        email,
        password,
      });

      const data = response.data?.data;
      if (response.data.success && data?.requires_two_factor) {
        return { requiresTwoFactor: true, challengeToken: String(data.challenge_token) };
      }
      if (response.data.success && data) {
        await finishLogin(data);
        return { requiresTwoFactor: false };
      }
      throw new Error(response.data.error || 'Erro ao fazer login.');
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Erro ao fazer login.';
      throw new Error(message);
    }
  }, [finishLogin]);

  const verifyTwoFactor = useCallback(async (challengeToken: string, code: string) => {
    try {
      const response = await axios.post(`${getApiUrl()}/auth/login/2fa`, {
        challenge_token: challengeToken,
        code,
      });
      if (response.data.success && response.data.data) {
        await finishLogin(response.data.data);
        return;
      }
      throw new Error(response.data.error || 'Código de verificação inválido.');
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Código de verificação inválido.';
      throw new Error(message);
    }
  }, [finishLogin]);

  // ============================================
  // Setup (criar primeiro admin)
  // ============================================

  const setup = useCallback(async (data: { email: string; password: string; full_name: string; setup_key: string }) => {
    try {
      const response = await axios.post(`${getApiUrl()}/auth/setup`, data);

      if (response.data.success && response.data.data) {
        const { user: userData, token: authToken } = response.data.data;

        setToken(authToken);
        setUser(userData);
        setNeedsSetup(false);

        localStorage.setItem('auth_token', authToken);
        localStorage.setItem('auth_user', JSON.stringify(userData));
        localStorage.setItem('token', authToken);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('user', JSON.stringify({
          id: userData.id,
          name: userData.full_name,
          email: userData.email,
        }));

        markFreshLoginForZoomOnboarding();
      } else {
        throw new Error(response.data.error || 'Erro ao realizar setup.');
      }
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Erro ao realizar setup.';
      throw new Error(message);
    }
  }, []);

  // ============================================
  // Logout
  // ============================================

  const logout = useCallback(() => {
    // Chamar logout no backend (fire-and-forget)
    if (token) {
      axios.post(`${getApiUrl()}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }

    markNativeLogout();
    clearAuth();
  }, [token]);

  // ============================================
  // Refresh user data
  // ============================================

  const refreshUser = useCallback(async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${getApiUrl()}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.data) {
        setUser(response.data.data);
        localStorage.setItem('auth_user', JSON.stringify(response.data.data));
      }
    } catch (error) {
      console.error('Refresh user error:', error);
    }
  }, [token]);

  // ============================================
  // Switch unit (multi-unidade)
  // ============================================

  const switchUnit = useCallback(async (unitId: string) => {
    if (!token) throw new Error('Sessão expirada.');
    try {
      const response = await axios.post(
        `${getApiUrl()}/auth/switch-unit`,
        { unit_id: unitId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success && response.data.data) {
        const { user: userData, token: authToken } = response.data.data;
        setToken(authToken);
        setUser(userData);
        localStorage.setItem('auth_token', authToken);
        localStorage.setItem('auth_user', JSON.stringify(userData));
        localStorage.setItem('token', authToken);
        if (userData?.current_unit?.id) {
          localStorage.setItem('last_unit_id', userData.current_unit.id);
        }
        // Notificar a aplicação para invalidar caches/refetchar
        window.dispatchEvent(new CustomEvent('unit:changed', { detail: { unitId: userData?.current_unit?.id } }));
      } else {
        throw new Error(response.data.error || 'Erro ao trocar unidade.');
      }
    } catch (error: any) {
      const message = error.response?.data?.error || error.message || 'Erro ao trocar unidade.';
      throw new Error(message);
    }
  }, [token]);

  // ============================================
  // Acessar como outro usuário (impersonação auditada)
  // ============================================

  const applySession = useCallback((authToken: string, userData: AuthUser) => {
    setToken(authToken);
    setUser(userData);

    localStorage.setItem('auth_token', authToken);
    localStorage.setItem('auth_user', JSON.stringify(userData));
    localStorage.setItem('token', authToken);
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('user', JSON.stringify({
      id: userData.id,
      name: userData.full_name,
      email: userData.email,
    }));

    // A unidade ativa muda junto com o perfil: força refetch dos caches por unidade.
    window.dispatchEvent(
      new CustomEvent('unit:changed', { detail: { unitId: userData?.current_unit?.id } }),
    );
  }, []);

  const impersonateUser = useCallback(async (userId: string) => {
    if (!token) throw new Error('Sessão expirada.');
    try {
      const response = await axios.post(
        `${getApiUrl()}/auth/users/${userId}/impersonate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!response.data?.success || !response.data?.data) {
        throw new Error(response.data?.error || 'Erro ao acessar como usuário.');
      }

      const { user: userData, token: authToken, impersonated_by } = response.data.data;

      // Guardado antes de trocar a sessão, senão não há como voltar ao admin.
      localStorage.setItem(IMPERSONATION_ORIGIN_TOKEN, token);
      if (user) {
        localStorage.setItem(IMPERSONATION_ORIGIN_USER, JSON.stringify(user));
      }

      applySession(authToken, { ...userData, impersonated_by });
    } catch (error: any) {
      const message =
        error.response?.data?.error || error.message || 'Erro ao acessar como usuário.';
      throw new Error(message);
    }
  }, [token, user, applySession]);

  const stopImpersonation = useCallback(() => {
    const originToken = localStorage.getItem(IMPERSONATION_ORIGIN_TOKEN);
    const originUser = localStorage.getItem(IMPERSONATION_ORIGIN_USER);

    localStorage.removeItem(IMPERSONATION_ORIGIN_TOKEN);
    localStorage.removeItem(IMPERSONATION_ORIGIN_USER);

    // Encerra a sessão de suporte no backend (fire-and-forget).
    if (token) {
      axios.post(`${getApiUrl()}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }

    if (!originToken || !originUser) {
      // Sem a sessão de origem só resta refazer o login.
      clearAuth();
      return;
    }

    try {
      applySession(originToken, JSON.parse(originUser) as AuthUser);
    } catch {
      clearAuth();
    }
  }, [token, applySession]);

  // ============================================
  // Limpar autenticação
  // ============================================

  const clearAuth = () => {
    inboxConversationCache.clear();
    setToken(null);
    setUser(null);
    
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem(IMPERSONATION_ORIGIN_TOKEN);
    localStorage.removeItem(IMPERSONATION_ORIGIN_USER);
    
    // Limpar dados do sistema antigo
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    localStorage.removeItem('skip_auth');
  };

  // ============================================
  // Helpers computados
  // ============================================

  const isAuthenticated = !!token && !!user;
  const isAdmin = user?.role === 'admin' || user?.role === 'admin_master';
  const impersonatedBy = user?.impersonated_by || null;
  const isProfessional = user?.role === 'professional';
  const roleLabel = user ? getRoleLabel(user.role) : '';
  const defaultRoute = user ? getDefaultRoute(user.role) : '/login';

  const hasPermission = useCallback((path: string): boolean => {
    if (!user) return false;
    return hasUserRoutePermission(user, path);
  }, [user]);

  // ============================================
  // Contexto
  // ============================================

  const currentUnit = user?.current_unit || null;
  const availableUnits = user?.available_units || [];

  const value: AuthContextType = useMemo(() => ({
    user,
    token,
    isAuthenticated,
    isLoading,
    needsSetup,
    login,
    verifyTwoFactor,
    logout,
    setup,
    refreshUser,
    switchUnit,
    impersonateUser,
    stopImpersonation,
    isAdmin,
    impersonatedBy,
    isProfessional,
    hasPermission,
    roleLabel,
    defaultRoute,
    currentUnit,
    availableUnits,
  }), [
    user,
    token,
    isAuthenticated,
    isLoading,
    needsSetup,
    login,
    verifyTwoFactor,
    logout,
    setup,
    refreshUser,
    switchUnit,
    impersonateUser,
    stopImpersonation,
    isAdmin,
    impersonatedBy,
    isProfessional,
    hasPermission,
    roleLabel,
    defaultRoute,
    currentUnit,
    availableUnits,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================
// HOOK
// ============================================

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
